import React from 'react';
import { Card, Typography, Table, Button, Modal, Tooltip } from 'antd';
import { PlusCircleOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useInboundAcceptance } from '@contexts/operator/InboundAcceptanceContext';

const { Text } = Typography;

export default function ModePage({ scannedItem, onNavigate }) {
  const { t } = useTranslation();
  const { deleteReceiptItem } = useInboundAcceptance();

  const receiptItems = scannedItem?.receipt_items || [];
  const totalReceived = receiptItems.reduce((sum, ri) => sum + (ri.quantity || 0), 0);

  // 已上架的收貨紀錄無法修正（改小驗收數量會違反 putaway + adjusted ≤ quantity）。
  // 只有 putaway_quantity 為 0 的那幾筆能進修正流程；若全部都已上架就停用「修正驗收」卡。
  const editableReceiptItems = receiptItems.filter((ri) => (ri.putaway_quantity ?? 0) === 0);
  const editDisabled = receiptItems.length > 0 && editableReceiptItems.length === 0;

  const handleDeleteReceiptItem = (ri) => {
    Modal.confirm({
      title: t('operator.inbound_item.delete_receipt_confirm_title'),
      content: t('operator.inbound_item.delete_receipt_confirm_content', { quantity: ri.quantity }),
      okText: t('common.button.confirm', { defaultValue: '確認' }),
      okType: 'danger',
      cancelText: t('common.button.cancel', { defaultValue: '取消' }),
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      onOk: () => deleteReceiptItem(ri.id),
    });
  };

  const handleModeSelect = (mode) => {
    if (mode === 'add_batch') {
      onNavigate('form', { mode: 'add_batch' });
    } else if (mode === 'edit') {
      if (editDisabled) return;
      // 修正只考慮尚未上架的收貨紀錄；剛好一筆可修正時直接進 form 帶那一筆，否則進 select 頁逐筆挑。
      if (editableReceiptItems.length === 1) {
        onNavigate('form', { mode: 'edit', editingReceiptItem: editableReceiptItems[0] });
      } else {
        onNavigate('select');
      }
    }
  };

  const columns = [
    { title: '#', render: (_, __, idx) => idx + 1, width: 40 },
    { title: t('models.receipt_item.quantity'), dataIndex: 'quantity', width: 60 },
    { title: t('models.receipt_item.batch'), dataIndex: 'batch', render: (v) => v || '-' },
    { title: t('models.receipt_item.expiration_date'), dataIndex: 'expiration_date', render: (d) => d ? dayjs(d).format('YYYY-MM-DD') : '-' },
    { title: t('models.receipt_item.verifier_name'), dataIndex: 'verifier_name', render: (v) => v || '-', width: 80 },
    { title: t('models.receipt_item.received_at'), dataIndex: 'received_at', render: (d) => d ? dayjs(d).format('MM-DD HH:mm') : '-', width: 100 },
    {
      title: '', key: 'action', width: 40,
      render: (_, ri) => (
        <Button type="text" danger size="small" icon={<DeleteOutlined />}
          onClick={(e) => { e.stopPropagation(); handleDeleteReceiptItem(ri); }}
        />
      )
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        <Card
          hoverable
          style={{ flex: 1, textAlign: 'center', cursor: 'pointer' }}
          onClick={() => handleModeSelect('add_batch')}
        >
          <PlusCircleOutlined style={{ fontSize: 32, color: '#52c41a', marginBottom: 8 }} />
          <div><Text strong>{t('operator.inbound_item.mode_add_batch')}</Text></div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('operator.inbound_item.mode_add_batch_desc')}
          </Text>
        </Card>
        <Tooltip title={editDisabled ? t('operator.inbound_item.mode_edit_disabled_putaway') : ''}>
          <Card
            hoverable={!editDisabled}
            style={editDisabled
              ? { flex: 1, textAlign: 'center', opacity: 0.5, cursor: 'not-allowed' }
              : { flex: 1, textAlign: 'center', cursor: 'pointer' }}
            onClick={editDisabled ? undefined : () => handleModeSelect('edit')}
          >
            <EditOutlined style={{ fontSize: 32, color: '#1890ff', marginBottom: 8 }} />
            <div><Text strong>{t('operator.inbound_item.mode_edit')}</Text></div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('operator.inbound_item.mode_edit_desc')}
            </Text>
          </Card>
        </Tooltip>
      </div>

      <div>
        <Text strong style={{ marginBottom: 8, display: 'block' }}>
          {t('operator.inbound_item.previous_receipts')} ({receiptItems.length})
          <Text type="secondary" style={{ fontWeight: 'normal', marginLeft: 8 }}>
            {t('operator.inbound_item.received')}: {totalReceived}
          </Text>
        </Text>
        <Table
          dataSource={receiptItems}
          columns={columns}
          rowKey="id"
          pagination={false}
          size="small"
        />
      </div>
    </div>
  );
}
